import { useCallback, useEffect, useState } from "react";

export type ThemeMode = "light" | "dark";

const STORAGE_KEY = "dmy.theme";

// Reserved for future routes that must ignore the theme (e.g., embedded
// branded landing pages). Currently empty — auth + signup honor the user's
// choice like every other route.
const LIGHT_ONLY_PREFIXES: string[] = [];

export function isLightOnlyRoute(pathname: string): boolean {
  return LIGHT_ONLY_PREFIXES.some((p) => pathname.startsWith(p));
}

/**
 * Resolve the stored preference. New visitors default to LIGHT — the dark
 * mode is opt-in only via the theme toggle. Users who previously selected
 * dark keep their choice (preserved in localStorage). Legacy values that
 * aren't strictly `"light"` or `"dark"` fall back to light too.
 */
function readStoredTheme(): ThemeMode {
  if (typeof window === "undefined") return "light";
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === "dark") return "dark";
  return "light";
}

function computeEffectiveTheme(mode: ThemeMode, pathname: string): ThemeMode {
  if (isLightOnlyRoute(pathname)) return "light";
  return mode;
}

function applyThemeToDocument(effective: ThemeMode) {
  if (typeof document === "undefined") return;
  const html = document.documentElement;
  const current = html.getAttribute("data-theme");
  if (current === effective) return;

  // Brief transition class so color changes animate rather than snap.
  html.classList.add("theme-transitioning");
  html.setAttribute("data-theme", effective);
  html.style.colorScheme = effective;
  window.setTimeout(() => {
    html.classList.remove("theme-transitioning");
  }, 200);
}

interface UseThemeResult {
  theme: ThemeMode;
  setTheme: (mode: ThemeMode) => void;
  effectiveTheme: ThemeMode;
  toggleTheme: () => void;
  isLightLocked: boolean;
}

/**
 * Theme state + persistence. Two modes only: `light` and `dark`. First visit
 * picks the user's OS preference as the initial value. Click flips to the
 * opposite. Auth routes force light regardless of setting.
 */
export function useTheme(): UseThemeResult {
  const [theme, setThemeState] = useState<ThemeMode>(() => readStoredTheme());
  const [pathname, setPathname] = useState<string>(
    () => (typeof window !== "undefined" ? window.location.pathname : "/")
  );

  // Apply the resolved theme whenever inputs change
  useEffect(() => {
    const effective = computeEffectiveTheme(theme, pathname);
    applyThemeToDocument(effective);
  }, [theme, pathname]);

  // Track pathname changes (react-router pushes history without reload)
  useEffect(() => {
    if (typeof window === "undefined") return;
    const update = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", update);
    // Patch pushState/replaceState to emit a synthetic event so SPA navigation
    // triggers a recomputation.
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function (...args) {
      const ret = origPush.apply(this, args as Parameters<typeof origPush>);
      window.dispatchEvent(new Event("dmy:locationchange"));
      return ret;
    };
    window.history.replaceState = function (...args) {
      const ret = origReplace.apply(this, args as Parameters<typeof origReplace>);
      window.dispatchEvent(new Event("dmy:locationchange"));
      return ret;
    };
    window.addEventListener("dmy:locationchange", update);
    return () => {
      window.removeEventListener("popstate", update);
      window.removeEventListener("dmy:locationchange", update);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, []);

  const setTheme = useCallback((mode: ThemeMode) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* ignore quota / private-mode errors */
    }
    setThemeState(mode);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next: ThemeMode = prev === "dark" ? "light" : "dark";
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const effectiveTheme = computeEffectiveTheme(theme, pathname);
  const isLightLocked = isLightOnlyRoute(pathname);

  return { theme, setTheme, effectiveTheme, toggleTheme, isLightLocked };
}

/**
 * Run once at app-bootstrap time — BEFORE React renders — to apply the stored
 * theme (or OS preference if unset) to <html data-theme=...> so the first
 * paint already matches. Avoids the light-theme flash on dark-mode reloads.
 */
export function bootstrapTheme(): void {
  if (typeof window === "undefined") return;
  const mode = readStoredTheme();
  const effective = computeEffectiveTheme(mode, window.location.pathname);
  document.documentElement.setAttribute("data-theme", effective);
  document.documentElement.style.colorScheme = effective;
}
